import type { Campaign, CampaignMember } from './components/types';
import { fetchLauncherCampaigns, fetchLauncherCampaign } from '../_shared/launcher-campaign-schema';
import { fetchJson, FetchJsonError } from '../_shared/fetch-json';
import {
  CampaignStatusesSchema,
  ScorecardTemplateSchema,
  ScorecardTemplatesSchema,
  LaunchedCampaignsSchema,
  WaterfallResponseSchema,
  SaveWaterfallResponseSchema,
  EnrichmentFlagSchema,
  EnrichmentFlagsSchema,
  LaunchDispatchResponseSchema,
  RelaunchResponseSchema,
  TestDispatchResponseSchema,
  ReenrichResponseSchema,
  DispatchLogSchema,
  WipeDispatchLogSchema,
  CampaignTypeConfigsSchema,
  WebhookSettingsSchema,
  WaterfallConfigsSchema,
  type DispatchLogRow,
  type CampaignTypeConfig,
  type WebhookSettings,
  type WaterfallConfigRow,
  type ScorecardTemplate,
  type EnrichmentFlag,
  type StatusMapping,
  type WaterfallRow,
} from './wire-schemas';
import {
  fetchLauncherCampaignMembers,
  fetchLauncherSyncStatus,
  syncLauncherCampaignMembers,
  type LauncherMemberSyncResult,
  type LauncherSyncStatus,
} from '../_shared/launcher-campaign-member-schema';

export type SyncStatus = LauncherSyncStatus;
export type { DispatchLogRow, CampaignTypeConfig, WebhookSettings, WaterfallConfigRow };

type ApiFetch = (input: RequestInfo | URL, init?: RequestInit & { timeoutMs?: number }) => Promise<Response>;

/**
 * Campaign reads delegate to `_shared/launcher-campaign-schema.ts` — the one
 * PARSED fetcher launcher, playbooks and playhouse all share, so the cache
 * under `['launcher','campaigns']` can only ever hold one shape (QA round 8,
 * U2; see that module's header for the crash this prevents). These thin
 * wrappers stay so v1's call sites keep their import path.
 */
export async function fetchCampaigns(apiFetch: ApiFetch): Promise<Campaign[]> {
  return fetchLauncherCampaigns(apiFetch);
}

export async function fetchCampaign(apiFetch: ApiFetch, id: string): Promise<Campaign> {
  return fetchLauncherCampaign(apiFetch, id);
}

export async function fetchCampaignMembers(apiFetch: ApiFetch, id: string): Promise<CampaignMember[]> {
  return fetchLauncherCampaignMembers(apiFetch, id);
}

export async function fetchCampaignStatuses(apiFetch: ApiFetch, id: string): Promise<string[]> {
  return fetchJson(apiFetch, CampaignStatusesSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(id)}/statuses`);
}

export async function fetchTemplates(apiFetch: ApiFetch, campaignType?: string): Promise<ScorecardTemplate[]> {
  const url = campaignType
    ? `/api/tools/launcher/templates?${new URLSearchParams({ campaign_type: campaignType })}`
    : '/api/tools/launcher/templates';
  return fetchJson(apiFetch, ScorecardTemplatesSchema, url);
}

export async function patchCampaignStatus(
  apiFetch: ApiFetch,
  id: string,
  launcher_status: 'draft' | 'launched',
): Promise<void> {
  await apiFetch(`/api/tools/launcher/campaigns/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ launcher_status }),
  });
}

export async function fetchLaunchedCampaigns(apiFetch: ApiFetch) {
  return fetchJson(apiFetch, LaunchedCampaignsSchema, '/api/tools/launcher/launched-campaigns');
}

export async function fetchWaterfallRows(apiFetch: ApiFetch, id: string): Promise<{ rows: WaterfallRow[] | null; status_mapping: StatusMapping | null }> {
  return fetchJson(apiFetch, WaterfallResponseSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(id)}/waterfall`);
}

export async function createTemplate(
  apiFetch: ApiFetch,
  payload: {
    name: string;
    campaign_type: string;
    /** Passed through verbatim; a duplicate of a template with corrupt rows must still copy. */
    rows: readonly unknown[];
    source?: 'template' | 'launch';
    campaign_sfdc_id?: string;
    campaign_name?: string;
  },
): Promise<ScorecardTemplate> {
  // `apiFetch` is the throwing mini-app boundary (`_shared/hooks/useApi.ts`):
  // on any `!res.ok` it throws a `FetchErrorLike` carrying `status` before
  // control reaches here. The 409-duplicate-name UX (WaterfallConfig.tsx
  // checks `err.message === 'duplicate_template_name'`) is preserved by
  // catching the thrown error and re-throwing under that exact message.
  try {
    return await fetchJson(apiFetch, ScorecardTemplateSchema, '/api/tools/launcher/templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    if (err instanceof FetchJsonError && err.kind === 'http' && err.status === 409) {
      throw new Error('duplicate_template_name');
    }
    throw err;
  }
}

export async function saveWaterfallRows(
  apiFetch: ApiFetch,
  id: string,
  rows: WaterfallRow[],
  statusMapping?: StatusMapping,
): Promise<{ rows: WaterfallRow[]; status_mapping: StatusMapping | null }> {
  return fetchJson(apiFetch, SaveWaterfallResponseSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(id)}/waterfall`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows, status_mapping: statusMapping }),
  });
}

export async function fetchEnrichmentFlags(apiFetch: ApiFetch): Promise<EnrichmentFlag[]> {
  return fetchJson(apiFetch, EnrichmentFlagsSchema, '/api/tools/launcher/enrichment-flags');
}

export async function createEnrichmentFlag(
  apiFetch: ApiFetch,
  payload: Omit<EnrichmentFlag, 'id'>,
): Promise<EnrichmentFlag> {
  return fetchJson(apiFetch, EnrichmentFlagSchema, '/api/tools/launcher/enrichment-flags', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function updateEnrichmentFlag(
  apiFetch: ApiFetch,
  id: string,
  payload: Partial<Omit<EnrichmentFlag, 'id'>>,
): Promise<EnrichmentFlag> {
  return fetchJson(apiFetch, EnrichmentFlagSchema, `/api/tools/launcher/enrichment-flags/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function deleteEnrichmentFlag(apiFetch: ApiFetch, id: string): Promise<void> {
  await apiFetch(`/api/tools/launcher/enrichment-flags/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function reorderEnrichmentFlags(apiFetch: ApiFetch, ids: string[]): Promise<void> {
  await apiFetch('/api/tools/launcher/enrichment-flags', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
}

export async function launchDispatch(
  apiFetch: ApiFetch,
  campaignId: string,
  payload: { waterfall_rows: WaterfallRow[]; status_mapping: StatusMapping; direct?: boolean },
) {
  return fetchJson(apiFetch, LaunchDispatchResponseSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/launch-dispatch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, direct: payload.direct ?? false }),
    timeoutMs: 120_000,
  });
}

export async function relaunchCampaign(apiFetch: ApiFetch, campaignId: string) {
  return fetchJson(apiFetch, RelaunchResponseSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/relaunch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    timeoutMs: 120_000,
  });
}

export async function testDispatch(
  apiFetch: ApiFetch,
  campaignId: string,
  payload: { row_name: string; member_ids: string[]; row: WaterfallRow; status_mapping: StatusMapping },
) {
  return fetchJson(apiFetch, TestDispatchResponseSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/test-dispatch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function fetchDispatchLog(
  apiFetch: ApiFetch,
  campaignId: string,
  params?: { dispatch_id?: string; is_test?: boolean },
): Promise<DispatchLogRow[]> {
  const url = new URL(`/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/dispatch-log`, 'http://x');
  if (params?.dispatch_id) url.searchParams.set('dispatch_id', params.dispatch_id);
  if (params?.is_test !== undefined) url.searchParams.set('is_test', String(params.is_test));
  return fetchJson(apiFetch, DispatchLogSchema, url.pathname + url.search);
}

export async function wipeDispatchLog(apiFetch: ApiFetch, campaignId: string) {
  return fetchJson(apiFetch, WipeDispatchLogSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/dispatch-log`, {
    method: 'DELETE',
  });
}

export async function wipeAllDispatchLogs(apiFetch: ApiFetch) {
  return fetchJson(apiFetch, WipeDispatchLogSchema, '/api/tools/launcher/dispatch-log', { method: 'DELETE' });
}

export async function syncCampaignMembers(
  apiFetch: ApiFetch,
  id: string,
): Promise<LauncherMemberSyncResult> {
  return syncLauncherCampaignMembers(apiFetch, id, 60_000);
}

export async function reenrichDispatch(apiFetch: ApiFetch, campaignId: string, memberIds: string[]) {
  return fetchJson(apiFetch, ReenrichResponseSchema, `/api/tools/launcher/campaigns/${encodeURIComponent(campaignId)}/reenrich`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

// ── Campaign type config ──────────────────────────────────────────────────────

export async function fetchCampaignTypeConfigs(apiFetch: ApiFetch): Promise<CampaignTypeConfig[]> {
  return fetchJson(apiFetch, CampaignTypeConfigsSchema, '/api/tools/launcher/settings/campaign-types');
}

export async function updateCampaignTypeConfigs(
  apiFetch: ApiFetch,
  updates: Array<{ type: string; subtype: string | null; enabled: boolean }>,
): Promise<void> {
  await apiFetch('/api/tools/launcher/settings/campaign-types', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function fetchSyncStatus(apiFetch: ApiFetch): Promise<SyncStatus> {
  return fetchLauncherSyncStatus(apiFetch);
}

export async function triggerCampaignSync(apiFetch: ApiFetch): Promise<void> {
  await apiFetch('/api/tools/launcher/campaigns/sync', { method: 'POST', timeoutMs: 60_000 });
}

// ── Webhook settings ──────────────────────────────────────────────────────────

export async function fetchWebhookSettings(apiFetch: ApiFetch): Promise<WebhookSettings> {
  return fetchJson(apiFetch, WebhookSettingsSchema, '/api/tools/launcher/settings/webhooks');
}

export async function updateWebhookSettings(apiFetch: ApiFetch, settings: Partial<WebhookSettings>): Promise<WebhookSettings> {
  return fetchJson(apiFetch, WebhookSettingsSchema, '/api/tools/launcher/settings/webhooks', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
}

export async function updateTemplateRows(apiFetch: ApiFetch, id: string, rows: WaterfallRow[]): Promise<void> {
  await apiFetch(`/api/tools/launcher/templates/${encodeURIComponent(id)}/rows`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
}

export async function fetchWaterfallConfigs(apiFetch: ApiFetch): Promise<WaterfallConfigRow[]> {
  return fetchJson(apiFetch, WaterfallConfigsSchema, '/api/tools/launcher/waterfall-configs');
}

export async function updateWaterfallConfigRows(apiFetch: ApiFetch, campaignId: string, rows: WaterfallRow[]): Promise<void> {
  await apiFetch(`/api/tools/launcher/waterfall-configs/${encodeURIComponent(campaignId)}/rows`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rows }),
  });
}

export async function deleteTemplate(apiFetch: ApiFetch, id: string): Promise<void> {
  await apiFetch(`/api/tools/launcher/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function renameTemplate(apiFetch: ApiFetch, id: string, name: string): Promise<void> {
  await apiFetch(`/api/tools/launcher/templates/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}
